import React, { forwardRef, MutableRefObject, useEffect } from "react";
import { InputActionMeta } from "react-select";
import { ButtonBox, LoadingBox, SelectBox, TextBox } from "../..";
import {
  ChangeItemValueTypeEnum,
  ComputedTransactionItemSearchResultModel,
  DailyTransactionTypeEnum,
  HasFormIdModel,
  ItemPricingAndStockDetailResponseModel,
  ItemSearchTypeEnum,
  LookupItemModel, TransactionItemResponseModel,
  UserConfigurationModel, ValidationErrorModel,
} from "../../../models";
import {
  getLabelName,
  getLookUpItemValue,
  getOriginalValueInCurrency,
  handleSelectInputChange,
  isArabicCurrentLanguage,
} from "../../../utils";
import { handleGroupItemsBasedOnId } from "../common/businessLogic/commonBl";
import {
  addItem,
  getTextBoxNumericOrDefaultValue,
  handleCalculateItemRecordValuesUpdate,
  handleChangeNewRecordItemCalculation,
  handleComputeSearchAndFilterItems, handleTransactionTotalValues,
} from "../common/businessLogic/transactionBl";
import { validateAddItem } from "../common/businessLogic/validationBl";
import { useSystemConfigurationsContext } from "../common/hooks/configurations/useSystemConfigurationsContext";
import { useTransactionInitializationActions } from "../common/hooks/initializer/useTransactionInitializationActions";
import { useTransactionInitializationContext } from "../common/hooks/initializer/useTransactionInitializationContext";
import {
  useCalcTypeList,
  useCategoryList,
  useIsSalesComponent,
  useItemList,
  useItemPerCategoryList,
  useStoreList
} from "../common/hooks/initializer/useTransactionInitializationSelectors";
import { useTransactionActions } from "../common/hooks/transactions/useTransactionActions";
import {
  useTransactionItemObject,
  useTransactionObject
} from "../common/hooks/transactions/useTransactionSelectors";
import { transactionDetailInitialValues, transactionItemInitialValues } from "../common/initialValues/initialValues";

interface RegisterTransactionItemProps extends HasFormIdModel {
  transactionType: DailyTransactionTypeEnum;
  lookupRefs: Record<string, React.MutableRefObject<any>>;
}
export const RegisterTransactionItem = forwardRef(
  ({ transactionType, lookupRefs, formId }: RegisterTransactionItemProps, ref
  ) => {
    //#region  state
    const request = useTransactionObject();
    const { handleSetItemsPerCategoryList, handleSetItemList } = useTransactionInitializationActions();
    const { handleSetObject, handleSetTransactionItemObject, handleSetValidationErrors } = useTransactionActions();
    const { isLoaded: isTransactionInitializationLoaded, hasSingleStore, defaultStoreId } = useTransactionInitializationContext({ transactionType, formId });
    const { isLoaded: isConfigurationLoaded, userTransactionSetting, userSetting, companySetting } = useSystemConfigurationsContext({ transactionType, formId });
    const calcTypeList = useCalcTypeList();
    const itemList = useItemList();
    const itemsPerCategoryList = useItemPerCategoryList();
    const categoryList = useCategoryList();
    const storeList = useStoreList();
    const isSalesComponent = useIsSalesComponent();
    const transactionItemObject = useTransactionItemObject();
    //#endregion
    //#region variables
    const isArabic = isArabicCurrentLanguage();
    const discountPercentageMaxValue: number = 50;
    const {
      StoreSelectBoxMultiselectRef,
      itemSelectBoxMultiselectRef,
      itemCategorySelectBoxMultiselectRef
    } = lookupRefs;
    //#endregion
    //#region functions
    const handleAddItem = async (
      itemRequest: TransactionItemResponseModel
    ) => {
      itemRequest.Transaction_Type = Number(transactionType);
      itemRequest = await addItem(
        itemRequest,
        request!,
        itemList && itemList.length > 0 ? itemList : itemsPerCategoryList,
        {
          userSetting: userSetting!,
          companySetting: companySetting!,
          userTransactionSetting: userTransactionSetting!,
          userTransactionSettings: [],
          systemModulesConfigurations: [],
          genericSystemConfiguration: null
        }
      );
      const errorList: ValidationErrorModel[] = await validateAddItem(
        itemRequest,
        {
          userSetting: userSetting!,
          companySetting: companySetting!,
          userTransactionSetting: userTransactionSetting!,
          userTransactionSettings: [],
          systemModulesConfigurations: [],
          genericSystemConfiguration: null
        }
      );
      if (errorList !== null && errorList.length !== 0) {
        handleSetValidationErrors(errorList);
        return;
      } else {
        handleSetValidationErrors([]);
      }
      request!.TransactionDetaillist = handleGroupItemsBasedOnId(
        request?.TransactionDetaillist ?? [],
        itemRequest,
        {
          userSetting: userSetting!,
          companySetting: companySetting!,
          userTransactionSetting: userTransactionSetting!,
          userTransactionSettings: [],
          systemModulesConfigurations: [],
          genericSystemConfiguration: null
        }
      );
      const dataObject = handleTransactionTotalValues(request!);
      handleSetObject({
        ...dataObject,
        TransactionDetaillist: [...dataObject.TransactionDetaillist],
      });
      // itemCategorySelectBoxMultiselectRef.current &&
      // itemCategorySelectBoxMultiselectRef.current.clearValue();
      // itemSelectBoxMultiselectRef.current &&
      // itemSelectBoxMultiselectRef.current.clearValue();
      const itemObject = {
        ...itemRequest,
        ItemDiscount: 0,
        ItemDiscountInCurrency: 0,
        originalItemDiscountInCurrency: 0,
        ItemDiscountRatio: 0,
        Quantity: 1,
      }
      handleSetTransactionItemObject({
        ...itemObject
      });
    };
    const handleItemSearch = async ({
      searchValue,
      searchType,
      transactionItemObject: customTransactionItemObject,
    }: {
      searchValue?: string | null;
      searchType: ItemSearchTypeEnum;
      transactionItemObject?: TransactionItemResponseModel | null;
    }) => {
      const effectiveTransactionItemObject =
        customTransactionItemObject ?? transactionItemObject ?? transactionItemInitialValues;
      const result: ComputedTransactionItemSearchResultModel = await handleComputeSearchAndFilterItems({
        itemList,
        itemPerCategoryList: itemsPerCategoryList,
        calcTypeList,
        transactionDetailObject: request ?? transactionDetailInitialValues,
        transactionItemObject: effectiveTransactionItemObject,
        userSetting: userSetting as unknown as UserConfigurationModel,
        transactionConfiguration: {
          transactionType: transactionType,
          isSalesComponent: isSalesComponent,
          hasSingleStore: hasSingleStore,
          defaultStoreId: defaultStoreId,
        },
        isArabic: isArabic,
        searchType: searchType,
        searchValue: searchValue,
      });

      handleSetItemList(result.items);
      handleSetItemsPerCategoryList(result.itemsPerCategory);
      if (result.updatedTransactionItem) {
        handleSetTransactionItemObject(result.updatedTransactionItem);
      }
    };
    //#endregion
    //#region useEffect
    useEffect(() => {
      const fill = async () => {
        if (!isTransactionInitializationLoaded || !isConfigurationLoaded) {
          return;
        }
        // 1) If no store selected, pick a default store
        if (transactionItemObject?.storeId == null && storeList?.length > 0) {
          const defaultStore = hasSingleStore || defaultStoreId
            ? storeList.find(s => s.value === defaultStoreId) || storeList[0]
            : storeList[0];
          handleSetTransactionItemObject({
            ...transactionItemInitialValues,
            storeId: Number(defaultStore.value),
          });
        }
        // 2) Single-store case – fill items once from itemList
        if (hasSingleStore && (itemsPerCategoryList == null || itemsPerCategoryList.length == 0)) {
          handleSetItemsPerCategoryList(itemList);
        }
        // 3) Multi-store: store + category already selected,
        //    but no items loaded yet → hydrate items list
        const hasStore = transactionItemObject?.storeId != null;

        if (!hasSingleStore &&
          hasStore &&
          (!itemsPerCategoryList || itemsPerCategoryList.length === 0)) {
          await handleItemSearch({
            searchValue: null,
            searchType: ItemSearchTypeEnum.none,
            transactionItemObject: transactionItemObject ?? transactionItemInitialValues,
          });
        }
      };
      fill().then(() => {
      });
    }, [request?.defaultStoreId, transactionItemObject?.storeId, storeList, isTransactionInitializationLoaded, isConfigurationLoaded]);

    //#endregion
    //#region html
    if (!isTransactionInitializationLoaded || !isConfigurationLoaded) {
      return <LoadingBox />;
    }
    return (
      <>
        <div
          // className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4"
          className="row g-3"
        >
          {!hasSingleStore &&
            <div className="col-12 col-md-4">
              <SelectBox
                id="StoreSelectBox"
                labelName={getLabelName("Store")}
                isSingleSelect={true}
                selectedValues={
                  transactionItemObject?.storeId != null
                    ? [transactionItemObject?.storeId.toString()]
                    : []
                }
                source={storeList}
                //@ts-ignore
                multiselectRef={(node: MutableRefObject<any>) =>
                  node ? (StoreSelectBoxMultiselectRef.current = node) : null
                }
                onStatusChange={async (e: LookupItemModel) => {
                  const value = getLookUpItemValue(e);
                  if (value !== null && value !== undefined) {
                    itemSelectBoxMultiselectRef.current && itemSelectBoxMultiselectRef.current.clearValue();
                    itemCategorySelectBoxMultiselectRef.current && itemCategorySelectBoxMultiselectRef.current.clearValue();
                    handleSetTransactionItemObject({
                      ...transactionItemInitialValues,
                      storeId: Number(value),
                      Categ_ID: null,
                    });
                  } else {
                    handleSetTransactionItemObject({
                      ...transactionItemInitialValues,
                      storeId: null,
                      Categ_ID: null,
                    });
                  }
                  await handleItemSearch({ searchValue: null, searchType: ItemSearchTypeEnum.none });
                }}
              />
            </div>
          }
          <div className="col-12 col-md-4">
            <SelectBox
              id="categorySelectBox"
              labelName={getLabelName("Category")}
              isSingleSelect={true}
              selectedValues={
                transactionItemObject?.Categ_ID !== null &&
                  transactionItemObject?.Categ_ID !== undefined
                  ? [transactionItemObject?.Categ_ID.toString()]
                  : []
              }
              source={categoryList}
              //multiselectRef={itemCategorySelectBoxMultiselectRef}
              //@ts-ignore
              multiselectRef={(node: MutableRefObject<any>) =>
                node ? (itemCategorySelectBoxMultiselectRef.current = node) : null
              }
              onStatusChange={async (e: LookupItemModel) => {
                const value = getLookUpItemValue(e);
                if (value != null) {
                  itemSelectBoxMultiselectRef.current && itemSelectBoxMultiselectRef.current.clearValue();
                  handleSetTransactionItemObject({
                    ...transactionItemObject!,
                    Categ_ID: Number(value),
                  });
                } else {
                  handleSetTransactionItemObject({
                    ...transactionItemObject!,
                    Categ_ID: null,
                  });
                }
                await handleItemSearch({
                  searchValue: null, searchType: ItemSearchTypeEnum.none,
                  transactionItemObject: { ...transactionItemObject!, Categ_ID: value != null ? Number(value) : null }
                });
              }}
            />
          </div>
          <div className="col-12 col-md-8">
            <SelectBox
              id="itemsSelectBox"
              labelName={getLabelName("Item")}
              isSingleSelect={true}
              isDisabled={false}
              selectedValues={
                transactionItemObject?.ItemUnit_ID !== null &&
                  transactionItemObject?.ItemUnit_ID !== undefined
                  ? [transactionItemObject?.ItemUnit_ID.toString()]
                  : []
              }
              source={
                (itemsPerCategoryList && itemsPerCategoryList.length > 0
                  ? itemsPerCategoryList
                  : itemList) ?? []
              }
              //multiselectRef={itemSelectBoxMultiselectRef}
              //@ts-ignore
              multiselectRef={(node: MutableRefObject<any>) =>
                node ? (itemSelectBoxMultiselectRef.current = node) : null
              }
              onInputChange={(inputValue: string, actionMeta: InputActionMeta) => {
                return handleSelectInputChange(inputValue, actionMeta, {
                  localList: itemsPerCategoryList || [],
                  apiSearchFn: async (searchValue) => {
                    await handleItemSearch({ searchValue: searchValue, searchType: ItemSearchTypeEnum.name });
                  },
                });
              }}
              onStatusChange={async (e: LookupItemModel) => {
                const value = getLookUpItemValue(e);
                const _item = itemsPerCategoryList.find(p => p.value == value);
                if (value != null) {
                  const itemOtherValue = _item?.otherValue as ItemPricingAndStockDetailResponseModel;
                  const transactionItemObj = await handleChangeNewRecordItemCalculation(
                    {
                      ...transactionItemObject ?? transactionItemInitialValues,
                      ItemUnit_ID: Number(value),
                      PriceCost: itemOtherValue.costPrice,
                      IsGatherItem: itemOtherValue.hasGatherItem,
                      TaxCalculatedValueOfGatherItem: itemOtherValue.taxCalculatedValueOfGatherItem,
                      barCode: _item?.otherValue?.barCode ?? null,
                    },
                    request!,
                    userTransactionSetting!,
                    transactionType,
                    itemList,
                    calcTypeList
                  );
                  transactionItemObj.ItemInstore_ID = _item?.otherValue?.itemInStoreId ?? null;
                  transactionItemObj.PriceCost = _item?.otherValue?.costPrice ?? 0;
                  handleSetTransactionItemObject(transactionItemObj);
                } else {
                  handleSetTransactionItemObject({
                    ...transactionItemInitialValues,
                    ItemUnit_ID: null,
                    ItemInstore_ID: null,
                    barCode: null
                  });
                }
              }}
            />
          </div>
          {userTransactionSetting?.ShowPrice && <div className="col-12 col-md-4">
            <TextBox
              labelName={getLabelName("Price")}
              isReadOnly={!userTransactionSetting?.EnableShangPrice}
              inputName={"Unit_PriceInCurrencyCalculated"}
              type="price"
              inputValue={transactionItemObject?.Unit_PriceInCurrencyCalculated}
              onChange={async (e: any) => {
                const value: number = getTextBoxNumericOrDefaultValue(e);
                handleSetTransactionItemObject({ ...transactionItemObject!, Unit_PriceInCurrencyCalculated: value, changeValueType: ChangeItemValueTypeEnum.ChangePriceInCurrency })
              }}
              onBlur={async () => {
                const obj = handleCalculateItemRecordValuesUpdate(
                  transactionItemObject!
                );
                handleSetTransactionItemObject(obj);
              }}
            />
          </div>
          }
          {userTransactionSetting?.ShowPrice && (
            <div className="col-12 col-md-4">
              <TextBox
                labelName={getLabelName("PriceSaleWithTax")}
                isReadOnly={!userTransactionSetting?.EnableShangPrice}
                inputName={"UnitPriceWithTaxInCurrency"}
                type="price"
                inputValue={transactionItemObject?.UnitPriceWithTaxInCurrency}
                onChange={async (e: any) => {
                  const value: number = getTextBoxNumericOrDefaultValue(e);
                  handleSetTransactionItemObject({
                    ...transactionItemObject!,
                    UnitPriceWithTaxInCurrency: value,
                    changeValueType: ChangeItemValueTypeEnum.ChangePriceWithTaxInCurrency
                  });
                }}
                onBlur={async () => {
                  const obj = handleCalculateItemRecordValuesUpdate(transactionItemObject!);
                  handleSetTransactionItemObject(obj);
                }}
              />
            </div>
          )}
          <div className="col-12 col-md-4">
            <TextBox
              labelName={getLabelName("Quantity")}
              inputName={"Quantity"}
              type="quantity"
              inputValue={transactionItemObject?.Quantity}
              onChange={async (e: any) => {
                const value: number = getTextBoxNumericOrDefaultValue(e);
                handleSetTransactionItemObject({ ...transactionItemObject!, Quantity: value });
              }}
            />
          </div>
          {userTransactionSetting?.EnableDiscount &&
            <div className="col-12 col-md-2">
              <TextBox
                labelName={getLabelName("Discount")}
                isReadOnly={!userTransactionSetting?.EnableDiscount}
                inputName={"ItemDiscountInCurrency"}
                type="price"
                inputValue={transactionItemObject?.ItemDiscountInCurrency}
                onChange={async (e: any) => {
                  const value: number = getTextBoxNumericOrDefaultValue(e);
                  const itemDiscount = getOriginalValueInCurrency(value, request?.ValueCurrency || 0);
                  handleSetTransactionItemObject({ ...transactionItemObject!, ItemDiscount: itemDiscount, ItemDiscountInCurrency: value, changeValueType: ChangeItemValueTypeEnum.None });
                }}
              />
            </div>
          }
          {userTransactionSetting?.EnableDiscount &&
            <div className="col-12 col-md-2">
              <TextBox
                labelName={getLabelName("Discount %")}
                isReadOnly={!userTransactionSetting?.EnableDiscount}
                inputName={"ItemDiscountRatio"}
                type="price"
                inputValue={transactionItemObject?.ItemDiscountRatio}
                onChange={async (e: any) => {
                  const value: number = getTextBoxNumericOrDefaultValue(e);
                  if (value <= discountPercentageMaxValue) {
                    handleSetTransactionItemObject({ ...transactionItemObject!, ItemDiscountRatio: value });
                  }
                }}
              />
            </div>
          }
          {userTransactionSetting?.EnableButonAdd &&
            // <div className="row mt-3">
            <div className="col-12 d-flex justify-content-end">
              <ButtonBox
                iconType="plus"
                id="addItemBtn"
                variant=""
                className="btn-gradient-info me-2"
                size="sm"
                onClick={async () => {
                  await handleAddItem(transactionItemObject!);
                }}
              >
                {getLabelName("Add")}
              </ButtonBox>
            </div>
            // </div>
          }
        </div>
      </>
    );
    //#endregion
  });

// // Component 1: Searching for items
// <SelectBox
//     onInputChange={(inputValue, actionMeta) => {
//       return handleSelectInputChange(inputValue, actionMeta, {
//         localList: itemsPerCategoryList || [],
//         apiSearchFn: async (searchValue) => {
//           // This component uses handleSearchAndFilterItems
//           await handleSearchAndFilterItems({
//             setItemsState: setItemList,
//             setItemsPerCategoryState: setItemPerCategoryList,
//             // ...other parameters
//             searchType: ItemSearchTypeEnum.name,
//             searchValue: searchValue,
//           });
//         }
//       });
//     }}
// />
//
// // Component 2: Searching for customers
// <SelectBox
//     onInputChange={(inputValue, actionMeta) => {
//       return handleSelectInputChange(inputValue, actionMeta, {
//         localList: customerList || [],
//         // Custom filter function specific to customers
//         localFilterFn: (customer, search, isArabic) => {
//           return customer.fullName.toLowerCase().includes(search.toLowerCase()) ||
//               customer.phoneNumber.includes(search);
//         },
//         apiSearchFn: async (searchValue) => {
//           // This component uses a different search function
//           const results = await searchCustomersByNameOrPhone(searchValue);
//           setCustomerList(results);
//         }
//       });
//     }}
// />
//
// // Component 3: Searching for products with image preview
// <SelectBox
//     onInputChange={(inputValue, actionMeta) => {
//       return handleSelectInputChange(inputValue, actionMeta, {
//         localList: productList || [],
//         minChars: 2, // Different minimum character threshold
//         debounceTime: 500, // Different debounce time
//         apiSearchFn: async (searchValue) => {
//           // This component uses yet another search function
//           setIsLoading(true);
//           try {
//             const response = await fetchProductsWithImages(searchValue, categoryId);
//             setProductList(response.data);
//             setTotalResults(response.totalCount);
//           } finally {
//             setIsLoading(false);
//           }
//         }
//       });
//     }}
// />